// src/context/AuthContext.tsx
import React, { createContext, useContext, useMemo, useState } from "react";

export type Role = "Admin" | "Manager" | "User";

export type AuthUser = {
  email: string;
  name: string;
  role: Role;
  mustChangePassword?: boolean;
};

type AuthContextType = {
  user: AuthUser | null;
  login: (u: AuthUser) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = "auth";
const TOKEN_KEY   = "auth_token";
const USER_RAW    = "auth_user";

function restoreStoredUser(): AuthUser | null {
  if (typeof window === "undefined") return null;

  // 1) format canonique
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AuthUser;
      if (parsed && ["Admin", "Manager", "User"].includes(parsed.role)) {
        const norm = { ...parsed, mustChangePassword: !!parsed.mustChangePassword };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(norm));
        return norm;
      }
    }
  } catch {
    /* noop */
  }

  // 2) fallback: ancien format `auth_user`
  try {
    const rawUser = window.localStorage.getItem(USER_RAW);
    if (rawUser) {
      const u = JSON.parse(rawUser) as {
        email?: string;
        name?: string;
        role?: Role;
        must_change_password?: boolean;
        mustChangePassword?: boolean;
      };
      if (u?.email && u?.role && ["Admin", "Manager", "User"].includes(u.role)) {
        const norm: AuthUser = {
          email: u.email,
          name: u.name || "",
          role: u.role,
          mustChangePassword: !!(u.mustChangePassword ?? u.must_change_password),
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(norm));
        return norm;
      }
    }
  } catch {
    /* noop */
  }

  return null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => restoreStoredUser());

  const login = (u: AuthUser) => {
    const norm = { ...u, mustChangePassword: !!u.mustChangePassword };
    setUser(norm);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(norm));
  };

  const logout = () => {
    setUser(null);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(USER_RAW);
    }
  };

  const value = useMemo(() => ({ user, login, logout }), [user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
